// ============================================================================
// Plugin Overrides helpers (BUCKET G.6)
// ============================================================================
// Pure helpers for the TenantDetail "Plugin Overrides" panel. Factored out of
// the component so the PATCH-body construction is unit-testable without a
// jsdom / React Testing Library setup (Adam does not currently ship one).
// ============================================================================

import {
  FEATURE_CATALOG,
  getFeaturesForPlugin,
  getPluginBackedIds,
} from "../../../../src/shared/tier-feature-registry.js";

/**
 * The tri-state control value. "default" means "no override — fall through to
 * tier logic" (i.e. the pluginId key is absent from overrides). `true` forces
 * the plugin on even when the tier denies; `false` forces it off.
 */
export type PluginOverrideState = "default" | "on" | "off";

export interface PluginOverrideCandidate {
  pluginId: string;
  /** Comma-joinable list of human-readable feature labels this plugin backs. */
  featureLabels: string[];
}

/**
 * Derive the list of plugin ids a tenant can override, with the labels of the
 * features each plugin backs. Reads authoritatively from `FEATURE_CATALOG` +
 * `getPluginBackedIds()` — no local duplicate list.
 */
export function getPluginOverrideCandidates(): PluginOverrideCandidate[] {
  const ids = [...getPluginBackedIds()].sort();
  return ids.map((pluginId) => ({
    pluginId,
    featureLabels: getFeaturesForPlugin(pluginId).map((f) => f.label),
  }));
}

/**
 * Resolve the current override state for a given plugin id from a tenant
 * config object (shape: `{ plugins?: { overrides?: Record<string, boolean> } }`).
 */
export function resolvePluginOverrideState(
  config: { plugins?: { overrides?: Record<string, boolean> } } | undefined,
  pluginId: string,
): PluginOverrideState {
  const raw = config?.plugins?.overrides?.[pluginId];
  if (raw === true) return "on";
  if (raw === false) return "off";
  return "default";
}

/**
 * Build a PATCH body that sets / clears a single plugin override while
 * preserving the rest of the tenant config and the rest of the overrides map.
 *
 * When `newState === "default"`, the pluginId key is REMOVED from overrides
 * (not set to `null` or `undefined`) so the gate falls through to tier logic.
 *
 * Returns: `{ config: { ...existing, plugins: { overrides: ... } } }`
 */
export function buildOverridePatchBody<
  C extends Record<string, unknown> & {
    plugins?: { overrides?: Record<string, boolean> };
  },
>(
  currentConfig: C,
  pluginId: string,
  newState: PluginOverrideState,
): { config: C } {
  const existingOverrides = currentConfig.plugins?.overrides ?? {};
  const nextOverrides: Record<string, boolean> = { ...existingOverrides };

  if (newState === "default") {
    delete nextOverrides[pluginId];
  } else {
    nextOverrides[pluginId] = newState === "on";
  }

  const nextPlugins = { ...(currentConfig.plugins ?? {}), overrides: nextOverrides };

  const nextConfig = {
    ...currentConfig,
    plugins: nextPlugins,
  } as C;

  return { config: nextConfig };
}

// Re-export for panel component consumption.
export { FEATURE_CATALOG };
